import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { api } from "@/lib/api";

type DayPoint = {
  date: string;
  daily: number;
  orders: number;
  cumulative: number;
  crossed: boolean;
};

type OrderAfter = {
  date: string | null;
  order_code: string | null;
  extend_code: string | null;
  carrier: string | null;
  customer: string | null;
  phone: string | null;
  amount: number;
  source: string;
};

type Resp = {
  year: number;
  threshold: number;
  current_revenue: number;
  remaining_to_threshold: number;
  threshold_crossed: boolean;
  threshold_date: string | null;
  threshold_day_total: number;
  threshold_day_cumulative: number;
  threshold_amount_before: number;
  by_day: DayPoint[];
  orders_after_threshold: OrderAfter[];
  orders_after_count: number;
  orders_after_total: number;
};

const fmtVND = (v: number) => v.toLocaleString("vi-VN") + " đ";
const fmtDate = (s: string | null) =>
  s ? new Date(s + "T00:00:00").toLocaleDateString("vi-VN") : "—";

export function InvoiceThresholdPage() {
  const [year, setYear] = useState(new Date().getFullYear());

  const q = useQuery({
    queryKey: ["invoice-threshold", year],
    queryFn: async () =>
      (await api.get<Resp>("/invoice-threshold", { params: { year } })).data,
  });

  const pct = useMemo(() => {
    if (!q.data) return 0;
    return Math.min(100, (q.data.current_revenue / q.data.threshold) * 100);
  }, [q.data]);

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <h1 className="text-xl font-semibold text-slate-900 mb-1">Ngưỡng xuất hoá đơn (1 tỷ)</h1>
      <p className="text-sm text-slate-500 mb-5">Cảnh báo khi doanh thu vượt ngưỡng xuất hoá đơn theo luật.</p>
      <div className="text-sm text-gray-600 mb-4">
        Theo TT152/2025 và NĐ70/2025: Hộ kinh doanh có doanh thu năm ≥ 1 tỷ VND phải
        xuất hoá đơn điện tử cho mọi đơn phát sinh từ thời điểm đạt ngưỡng.
        <br />
        <b>Cộng dồn doanh thu</b>: POScake + đơn tạo tay VTP/JT + TikTok + Shopee
        (TikTok/Shopee gán vào ngày 1 mỗi tháng vì chỉ có dữ liệu tháng).
        <br />
        <b>Danh sách đơn cần xuất HĐ</b>: chỉ POScake + carrier-only (TikTok/Shopee đã
        có hoá đơn riêng trên platform).
      </div>

      <div className="mb-4">
        <label className="text-sm text-gray-600 mr-2">Năm:</label>
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="border rounded px-2 py-1 text-sm"
        >
          {[year - 1, year, year + 1].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>

      {q.isLoading && <div className="text-gray-500">Đang tải...</div>}

      {q.data && (
        <>
          {/* Status banner */}
          {q.data.threshold_crossed ? (
            <div className="bg-orange-50 border-2 border-orange-400 rounded p-4 mb-4">
              <div className="text-orange-800 font-semibold mb-1">
                🟠 ĐÃ VƯỢT NGƯỠNG 1 TỶ vào ngày {fmtDate(q.data.threshold_date)}
              </div>
              <div className="text-sm text-gray-700">
                Doanh thu ngày đạt ngưỡng:{" "}
                <b>{fmtVND(q.data.threshold_day_total)}</b> (cộng dồn:{" "}
                <b>{fmtVND(q.data.threshold_day_cumulative)}</b>). Tổng năm hiện tại:{" "}
                <b className="text-green-700">{fmtVND(q.data.current_revenue)}</b>.
              </div>
              <div className="text-xs text-red-700 mt-2">
                ⚠️ Mọi đơn phát sinh từ ngày {fmtDate(q.data.threshold_date)} trở đi
                cần được xuất hoá đơn điện tử ({q.data.orders_after_count} đơn /{" "}
                {fmtVND(q.data.orders_after_total)}).
              </div>
            </div>
          ) : (
            <div className="bg-green-50 border-2 border-green-400 rounded p-4 mb-4">
              <div className="text-green-800 font-semibold mb-1">
                ✅ CHƯA ĐẠT NGƯỠNG 1 TỶ
              </div>
              <div className="text-sm text-gray-700">
                Doanh thu năm {q.data.year} hiện tại:{" "}
                <b className="text-green-700">{fmtVND(q.data.current_revenue)}</b>.
                Còn cần thêm{" "}
                <b className="text-orange-700">
                  {fmtVND(q.data.remaining_to_threshold)}
                </b>{" "}
                nữa thì đạt ngưỡng phải xuất hoá đơn.
              </div>
            </div>
          )}

          {/* Progress bar */}
          <div className="bg-white border rounded p-3 mb-4">
            <div className="flex items-center justify-between text-xs mb-1">
              <span>0đ</span>
              <span className="font-semibold text-orange-700">1 tỷ (ngưỡng)</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-6 overflow-hidden relative">
              <div
                className={`h-6 ${
                  q.data.threshold_crossed ? "bg-orange-500" : "bg-green-500"
                } transition-all`}
                style={{ width: `${pct}%` }}
              />
              <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-white drop-shadow">
                {pct.toFixed(1)}% — {fmtVND(q.data.current_revenue)}
              </div>
            </div>
          </div>

          {/* Cumulative table */}
          <div className="bg-white border rounded mb-4">
            <div className="px-3 py-2 text-sm font-medium border-b bg-gray-50">
              Lịch sử cộng dồn doanh thu năm {q.data.year}
            </div>
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-gray-600 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-1.5">Ngày</th>
                    <th className="text-right px-3 py-1.5">Đơn</th>
                    <th className="text-right px-3 py-1.5">Doanh thu ngày</th>
                    <th className="text-right px-3 py-1.5">Cộng dồn</th>
                    <th className="text-center px-3 py-1.5">Trạng thái</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.by_day.map((d) => {
                    const isThresholdDay = d.date === q.data!.threshold_date;
                    return (
                      <tr
                        key={d.date}
                        className={`border-t ${
                          isThresholdDay
                            ? "bg-orange-100 font-semibold"
                            : d.crossed
                            ? "bg-orange-50/40"
                            : ""
                        }`}
                      >
                        <td className="px-3 py-1">{fmtDate(d.date)}</td>
                        <td className="px-3 py-1 text-right">{d.orders}</td>
                        <td className="px-3 py-1 text-right">{fmtVND(d.daily)}</td>
                        <td className="px-3 py-1 text-right font-mono">
                          {fmtVND(d.cumulative)}
                        </td>
                        <td className="px-3 py-1 text-center">
                          {isThresholdDay ? (
                            <span className="text-orange-700">⚠️ Vượt ngưỡng</span>
                          ) : d.crossed ? (
                            <span className="text-orange-600 text-[10px]">
                              Cần xuất HĐ
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Orders after threshold */}
          {q.data.threshold_crossed && q.data.orders_after_threshold.length > 0 && (
            <div className="bg-white border-2 border-red-300 rounded mb-4">
              <div className="px-3 py-2 text-sm font-medium border-b bg-red-50 text-red-800">
                ⚠️ Đơn cần xuất hoá đơn điện tử ({q.data.orders_after_count} đơn /{" "}
                {fmtVND(q.data.orders_after_total)})
              </div>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-1.5">Ngày giao</th>
                      <th className="text-left px-3 py-1.5">Mã đơn</th>
                      <th className="text-left px-3 py-1.5">ĐVVC</th>
                      <th className="text-left px-3 py-1.5">Khách hàng</th>
                      <th className="text-left px-3 py-1.5">SĐT</th>
                      <th className="text-right px-3 py-1.5">Số tiền</th>
                      <th className="text-left px-3 py-1.5">Nguồn</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.orders_after_threshold.map((o, i) => (
                      <tr key={i} className="border-t hover:bg-gray-50">
                        <td className="px-3 py-1">{fmtDate(o.date)}</td>
                        <td className="px-3 py-1 font-mono">{o.order_code ?? "—"}</td>
                        <td className="px-3 py-1">{o.carrier ?? "—"}</td>
                        <td className="px-3 py-1 truncate max-w-[12rem]">
                          {o.customer ?? "—"}
                        </td>
                        <td className="px-3 py-1">{o.phone ?? "—"}</td>
                        <td className="px-3 py-1 text-right font-medium">
                          {fmtVND(o.amount)}
                        </td>
                        <td className="px-3 py-1">
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded ${
                              o.source === "POScake"
                                ? "bg-green-100 text-green-800"
                                : "bg-purple-100 text-purple-800"
                            }`}
                          >
                            {o.source}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
